import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { firebaseAuth } from '../../../lib/firebase';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import {
  clearStoredPortalEmail,
  describeSignInError,
  readStoredPortalEmail,
} from '../../../lib/portalSignIn';
import { PortalShell } from './PortalShell';
import { PortalSignIn } from './PortalSignIn';

// CLIENT PORTAL — /portal/finish. Completes the email-link handshake and gets
// out of the way.
//
// It exists as its own route rather than living on the portal home page so the
// awkward cases have somewhere to be handled: a link opened on a different
// device than it was requested from, a link used twice, an expired link. The
// home page stays about the client's accounts.

type State =
  | { kind: 'working' }
  | { kind: 'needs-email' }
  | { kind: 'failed'; message: string }
  | { kind: 'not-a-link' };

export function PortalFinish() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'working' });
  const [email, setEmail] = useState('');
  // ⚠️ StrictMode double-invokes effects in development. The oobCode is
  // SINGLE-USE, so a second attempt consumes an already-spent code and reports
  // "link already used" for a sign-in that actually worked. This guard is what
  // stops the dev experience contradicting the real one.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    const href = window.location.href;
    if (!isSignInWithEmailLink(firebaseAuth, href)) {
      setState({ kind: 'not-a-link' });
      return;
    }
    const stored = readStoredPortalEmail();
    if (!stored) {
      // Legitimate and common: requested on a laptop, opened on a phone.
      setState({ kind: 'needs-email' });
      return;
    }
    attempted.current = true;
    void complete(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function complete(address: string): Promise<void> {
    setState({ kind: 'working' });
    try {
      await signInWithEmailLink(firebaseAuth, address, window.location.href);
      clearStoredPortalEmail();
      // replace, so Back does not return to a spent link.
      navigate('/portal', { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? null;
      setState({ kind: 'failed', message: describeSignInError(code) });
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    attempted.current = true;
    void complete(trimmed);
  };

  if (state.kind === 'working') {
    return (
      <PortalShell>
        <CenteredSpinner />
      </PortalShell>
    );
  }

  // A spent or bad link, or someone arriving here directly: both end at the
  // sign-in form, because requesting a fresh link is the fix in either case.
  if (state.kind === 'failed') return <PortalSignIn notice={state.message} />;
  if (state.kind === 'not-a-link') return <PortalSignIn />;

  return (
    <PortalShell>
      <div className="mx-auto max-w-md">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Confirm your email
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It looks like you opened this link on a different device. Enter the address you
          requested it for and we'll finish signing you in.
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-3">
          <label htmlFor="confirm-email" className="block text-sm font-medium text-foreground">
            Email address
          </label>
          <Input
            id="confirm-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourcompany.com"
            required
          />
          <Button type="submit" disabled={!email.trim()} className="w-full">
            Continue
          </Button>
        </form>
      </div>
    </PortalShell>
  );
}
